import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { supabase } from '../lib/supabase.ts';
import { useHistory, useParams } from 'react-router-dom';

interface ClubLeaderRouteProps {
  children: React.ReactNode;
}

const ClubLeaderRoute: React.FC<ClubLeaderRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const history = useHistory();
  const { slug } = useParams<{ slug: string }>();
  const [isLeader, setIsLeader] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkLeaderPermission = async () => {
      if (!user || !slug) {
        setIsLeader(false);
        setLoading(false);
        return;
      }

      try {
        // 사용자 권한 확인 (관리자인지 체크)
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();

        if (!userError && userData?.role === 'admin') {
          setIsLeader(true);
          setLoading(false);
          return;
        }

        // 먼저 클럽 정보를 가져옴
        const { data: labData, error: labError } = await supabase
          .from('labs')
          .select('id, creator_id')
          .eq('slug', slug)
          .single();

        if (labError) {
          console.error('클럽 정보 조회 오류:', labError);
          setIsLeader(false);
          setLoading(false);
          return;
        }

        // 클럽 생성자인지 확인
        if (labData.creator_id === user.id) {
          setIsLeader(true);
          setLoading(false);
          return;
        }

        // 클럽 멤버 중 리더 권한이 있는지 확인
        const { data: memberData, error: memberError } = await supabase
          .from('lab_members')
          .select('role')
          .eq('lab_id', labData.id)
          .eq('user_id', user.id)
          .eq('status', 'active')
          .single();

        if (memberError) {
          console.error('멤버 권한 조회 오류:', memberError);
          setIsLeader(false);
        } else {
          setIsLeader(memberData?.role === 'leader');
        }
      } catch (error) {
        console.error('권한 확인 중 오류:', error);
        setIsLeader(false);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      checkLeaderPermission();
    }
  }, [user, slug, authLoading]);

  // 로딩 중
  if (authLoading || loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontFamily: "'Crimson Text', serif",
        }}
      >
        권한 확인 중...
      </div>
    );
  }

  // 로그인하지 않은 경우
  if (!user) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontFamily: "'Crimson Text', serif",
          textAlign: 'center',
          padding: '2rem',
        }}
      >
        <div
          style={{
            fontSize: '3rem',
            marginBottom: '1rem',
          }}
        >
          🔐
        </div>
        <h2
          style={{
            fontSize: '1.5rem',
            fontWeight: '300',
            color: '#2a2a2a',
            marginBottom: '1rem',
          }}
        >
          로그인이 필요합니다
        </h2>
        <p
          style={{
            fontSize: '1rem',
            color: '#666',
            marginBottom: '2rem',
            lineHeight: 1.5,
          }}
        >
          이 페이지는 로그인 후 클럽 리더만 접근할 수 있습니다.
        </p>
        <button
          onClick={() => history.push('/')}
          style={{
            background: '#8b7355',
            border: 'none',
            borderRadius: '25px',
            padding: '0.8rem 2rem',
            color: '#ffffff',
            fontSize: '1rem',
            cursor: 'pointer',
            fontWeight: '500',
          }}
        >
          홈으로 이동
        </button>
      </div>
    );
  }

  // 리더 권한이 없는 경우
  if (isLeader === false) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontFamily: "'Crimson Text', serif",
          textAlign: 'center',
          padding: '2rem',
        }}
      >
        <div
          style={{
            fontSize: '3rem',
            marginBottom: '1rem',
          }}
        >
          🚫
        </div>
        <h2
          style={{
            fontSize: '1.5rem',
            fontWeight: '300',
            color: '#2a2a2a',
            marginBottom: '1rem',
          }}
        >
          접근 권한이 없습니다
        </h2>
        <p
          style={{
            fontSize: '1rem',
            color: '#666',
            marginBottom: '2rem',
            lineHeight: 1.5,
          }}
        >
          이 페이지는 클럽 리더만 접근할 수 있습니다.
          <br />클럽 리더가 되려면 클럽 생성자에게 문의해주세요.
        </p>
        <button
          onClick={() => history.goBack()}
          style={{
            background: '#8b7355',
            border: 'none',
            borderRadius: '25px',
            padding: '0.8rem 2rem',
            color: '#ffffff',
            fontSize: '1rem',
            cursor: 'pointer',
            fontWeight: '500',
          }}
        >
          이전 페이지로 돌아가기
        </button>
      </div>
    );
  }

  // 리더 권한이 있는 경우 컴포넌트 렌더링
  return <>{children}</>;
};

export default ClubLeaderRoute;
